import { useState, useMemo } from 'react';
import DateRangeFilter from '../components/DateRangeFilter';

export const useSpreadsheetFiltering = ({ data, visibleColumnNames }) => {
  const [columnFilters, setColumnFilters] = useState({});
  const [globalSearch, setGlobalSearch] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Filtered data
  const filteredData = useMemo(() => {
    let result = (data || [])
      .map((item, index) => ({ ...item, _originalIndex: index }))
      .filter((row) => {
        let matchesColumnFilters = true;
        
        Object.keys(columnFilters).forEach((columnKey) => {
          const filterValues = columnFilters[columnKey];
          
          if ((columnKey === 'Date' || columnKey === 'Due Date') && filterValues) {
            const [startDate, endDate] = filterValues;
            if (startDate) {
              const rowDate = new Date(row[columnKey]);
              const start = new Date(startDate);
              
              if (!endDate) {
                if (
                  isNaN(rowDate.getTime()) || 
                  rowDate.toISOString().split('T')[0] !== start.toISOString().split('T')[0]
                ) {
                  matchesColumnFilters = false;
                }
              } else {
                const end = new Date(endDate);
                if (isNaN(rowDate.getTime()) || rowDate < start || rowDate > end) {
                  matchesColumnFilters = false;
                }
              }
            }
          } else if (filterValues && filterValues.length > 0) {
            if (!filterValues.includes(row[columnKey])) {
              matchesColumnFilters = false;
            }
          }
        });

        let matchesGlobalSearch = true;
        if (globalSearch.trim() !== '') {
          const searchValue = globalSearch.toLowerCase();
          matchesGlobalSearch = Object.keys(visibleColumnNames).some(
            (columnKey) => {
              return String(row[columnKey]).toLowerCase().includes(searchValue);
            }
          );
        }

        return matchesColumnFilters && matchesGlobalSearch;
      });

    if (sortColumn) {
      result.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn])
          return sortDirection === 'asc' ? -1 : 1;
        if (a[sortColumn] > b[sortColumn])
          return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    data,
    columnFilters,
    globalSearch,
    visibleColumnNames,
    sortColumn,
    sortDirection,
  ]);

  // Column filter options
  const columnFilterOptions = useMemo(() => {
    const options = {};
    const safeData = data || [];
    
    Object.keys(visibleColumnNames).forEach((columnKey) => {
      if (columnKey === 'Date' || columnKey === 'Due Date' || columnKey === 'Date Confirmed') {
        options[columnKey] = {
          isDateFilter: true,
          component: DateRangeFilter
        };
      } else {
        const uniqueValues = new Set();
        safeData.forEach((row) => {
          if (row[columnKey] !== undefined && row[columnKey] !== null) {
            uniqueValues.add(String(row[columnKey]));
          }
        });
        options[columnKey] = Array.from(uniqueValues).sort().map(value => ({
          value,
          label: String(value)
        }));
      }
    });
    
    return options;
  }, [data, visibleColumnNames]);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  return {
    filteredData,
    columnFilters,
    setColumnFilters,
    globalSearch,
    setGlobalSearch,
    sortColumn,
    sortDirection,
    columnFilterOptions,
    handleSort,
  };
};